import { format, startOfWeek, subDays, subWeeks } from 'date-fns';

export const formatTime = (timeString: string): string => {
  if (
    timeString.toLocaleLowerCase().includes('am') ||
    timeString.toLocaleLowerCase().includes('pm')
  ) {
    return timeString;
  }
  if (timeString.includes(':') && !timeString.includes('-')) {
    // eslint-disable-next-line prefer-const
    let [hours, minutes] = timeString.split(':');
    // Pad single-digit hours with a leading zero
    if (hours.length === 1) {
      hours = `0${hours}`;
    }
    const date = new Date(`2000-01-01T${hours}:${minutes}`);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  // For ISO date strings with timezone
  const date = new Date(timeString);

  // Convert to IST (UTC+5:30)
  const istOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  };
  const formattedTime = new Intl.DateTimeFormat('en-US', istOptions).format(
    date,
  );
  return formattedTime;
};

export const calculateDateRanges = (
  period: string,
): {
  thisPeriodStart: string;
  thisPeriodEnd: string;
  lastPeriodStart: string;
  lastPeriodEnd: string;
} => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  let thisPeriodStart: Date;
  let thisPeriodEnd: Date = today;
  let lastPeriodStart: Date;
  let lastPeriodEnd: Date;

  const setStartOfWeek = (date: Date, offset = 0): Date => {
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay() + offset);
    return start;
  };

  const setStartOfMonth = (date: Date, offset = 0): Date => {
    const start = new Date(date);
    start.setMonth(date.getMonth() + offset);
    start.setDate(1);
    return start;
  };

  switch (period.toLowerCase()) {
    case 'today':
      thisPeriodStart = new Date(today);
      thisPeriodStart.setHours(0, 0, 0, 0);
      thisPeriodEnd = today;
      lastPeriodStart = subDays(thisPeriodStart, 1);
      lastPeriodEnd = subDays(thisPeriodEnd, 1);
      break;

    case 'yesterday':
      thisPeriodStart = subDays(today, 1);
      thisPeriodStart.setHours(0, 0, 0, 0);
      thisPeriodEnd = subDays(today, 1);
      thisPeriodEnd.setHours(23, 59, 59, 999);
      lastPeriodStart = subDays(thisPeriodStart, 1);
      lastPeriodEnd = subDays(thisPeriodEnd, 1);
      break;

    case 'this_week':
      thisPeriodStart = startOfWeek(today, { weekStartsOn: 1 });
      thisPeriodEnd = today;
      lastPeriodStart = subWeeks(thisPeriodStart, 1);
      lastPeriodEnd = subDays(thisPeriodStart, 1);
      break;

    case 'last_week':
      thisPeriodStart = setStartOfWeek(today, -7);
      thisPeriodEnd = new Date(thisPeriodStart);
      thisPeriodEnd.setDate(thisPeriodStart.getDate() + 6);

      lastPeriodStart = setStartOfWeek(thisPeriodStart, -7);
      lastPeriodEnd = new Date(thisPeriodStart);
      lastPeriodEnd.setDate(thisPeriodStart.getDate() - 1);
      break;

    case 'this_month':
      thisPeriodStart = setStartOfMonth(today);
      thisPeriodEnd = thisPeriodStart < today ? today : thisPeriodStart;
      lastPeriodStart = setStartOfMonth(thisPeriodStart, -1);
      lastPeriodEnd = subDays(thisPeriodStart, 1);
      break;

    case 'last_month':
      thisPeriodStart = setStartOfMonth(today, -1);
      thisPeriodEnd = new Date(thisPeriodStart);
      thisPeriodEnd.setMonth(thisPeriodStart.getMonth() + 1);
      thisPeriodEnd.setDate(0);

      lastPeriodStart = setStartOfMonth(thisPeriodStart, -1);
      lastPeriodEnd = new Date(thisPeriodStart);
      lastPeriodEnd.setDate(0);
      break;

    case 'last_7days':
      thisPeriodStart = subDays(today, 6);
      thisPeriodStart.setHours(0, 0, 0, 0);
      thisPeriodEnd = today;
      lastPeriodStart = subDays(thisPeriodStart, 7);
      lastPeriodEnd = subDays(thisPeriodStart, 1);
      break;

    case 'last_15days':
      thisPeriodStart = subDays(today, 14);
      thisPeriodStart.setHours(0, 0, 0, 0);
      thisPeriodEnd = today;
      lastPeriodStart = subDays(thisPeriodStart, 15);
      lastPeriodEnd = subDays(thisPeriodStart, 1);
      break;

    default:
      throw new Error('Invalid period specified');
  }

  return {
    thisPeriodStart: format(thisPeriodStart, 'yyyy-MM-dd'),
    thisPeriodEnd: format(thisPeriodEnd, 'yyyy-MM-dd'),
    lastPeriodStart: format(lastPeriodStart, 'yyyy-MM-dd'),
    lastPeriodEnd: format(lastPeriodEnd, 'yyyy-MM-dd'),
  };
};

export const formatDuration = (hours: number): string => {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h} hr ${m} mins`;
  if (h > 0) return `${h} hr${h > 1 ? 's' : ''}`;
  return `${m} mins`;
};
